import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BaseModal from 'components/BaseModal';
import BlockingOverlay from 'components/BlockingOverlay';
import { TickerContext } from 'components/TickerProvider';
import { toISO, formatTimeDisplay } from 'lib/utils/date';
import { OptionList, OptionRow, OptionLabel, OptionTime } from './styled';

/**
 * BaseJobTimeDialog Component
 *
 * A reusable modal dialog for job time selection that eliminates duplication
 * between JobCloseAtDialog and JobStartAtDialog.
 *
 * @param {Object} props
 * @param {string} props.headerText - The dialog header text
 * @param {Array} props.optionsList - Array of time options to display
 * @param {string} props.tzid - Timezone ID for time formatting
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Function} props.onSelect - Function called when an option is selected
 * @returns {JSX.Element} The dialog component
 */
const BaseJobTimeDialog = ({
  headerText,
  optionsList,
  tzid,
  onClose,
  onSelect,
  isBusy = false,
  busyTitle,
}) => {
  const nowMs = useContext(TickerContext);

  // Local state for selected option
  const [selectedId, setSelectedId] = useState(null);

  /**
   * Auto-select the first enabled option when component mounts or options change
   * If the selected option is no longer enabled, reset the selection to null
   */
  useEffect(() => {
    const enabledOptions = optionsList.filter((opt) => {
      return !opt.disabled;
    });

    if (!enabledOptions?.length) {
      setSelectedId(null);
      return;
    }

    // If no option is currently selected, select the first enabled option
    if (!selectedId) {
      setSelectedId(enabledOptions[0].id);
      return;
    }

    // Check if the currently selected option is still enabled
    const selectedOption = optionsList.find((opt) => {
      return opt.id === selectedId;
    });
    if (!selectedOption || selectedOption.disabled) {
      // If selected option is disabled or doesn't exist, select the first enabled option
      setSelectedId(enabledOptions[0].id);
    }
  }, [selectedId, optionsList]);

  /**
   * Handles option selection
   * Only allows selection of enabled options
   *
   * @param {Object} opt - The selected option object
   */
  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }
    onClose();
  }, [isBusy, onClose]);

  const handleSelect = useCallback(
    (opt) => {
      if (isBusy || opt.disabled || opt.loading) {
        return;
      }
      setSelectedId(opt.id);
      // For "now" option, always use current time when selected
      const selectedValue = opt.id === 'now' ? toISO(nowMs) : opt.value;
      onSelect(selectedValue, opt);
    },
    [isBusy, nowMs, onSelect]
  );

  return (
    <>
      <BlockingOverlay isVisible={isBusy} title={busyTitle} />
      <BaseModal
        isOpen
        onClose={handleClose}
        header={headerText}
        details={
          <OptionList>
            {optionsList.map((opt) => {
              return (
                <OptionRow
                  key={opt.id}
                  selected={selectedId === opt.id}
                  disabled={opt.disabled}
                  onClick={() => {
                    handleSelect(opt);
                  }}
                  tabIndex={opt.disabled ? -1 : 0}
                  aria-disabled={opt.disabled}
                >
                  <OptionLabel
                    selected={selectedId === opt.id}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </OptionLabel>
                  <OptionTime
                    selected={selectedId === opt.id}
                    disabled={opt.disabled}
                  >
                    {opt.placeholder || formatTimeDisplay(opt.value, tzid)}
                  </OptionTime>
                </OptionRow>
              );
            })}
          </OptionList>
        }
        buttons={null}
      />
    </>
  );
};

BaseJobTimeDialog.propTypes = {
  headerText: PropTypes.string.isRequired,
  optionsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  tzid: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  isBusy: PropTypes.bool,
  busyTitle: PropTypes.node,
};

export default BaseJobTimeDialog;
